import styled, { css } from "styled-components";

/** 상단 액센트 스트립 색상 */
export const ACCENTS = {
  emerald: "#10b981",
  indigo: "#6366f1",
  zinc: "#71717a",
} as const;

/** 공통 컬러 토큰 (라이트 기준) */
const TOKENS = {
  bg: "#ffffff",
  bgWeak: "#f8fafc",
  border: "#e5e7eb",
  borderStrong: "#cbd5e1",
  text: "#0f172a",
  textWeak: "#334155",
  textMuted: "#64748b",
};

export const Container = styled.div<{ $accent?: keyof typeof ACCENTS }>`
  --accent: ${({ $accent }) => ($accent ? ACCENTS[$accent] : ACCENTS.zinc)};
  --stripe-h: 3px;

  --bg: ${TOKENS.bg};
  --bg-weak: ${TOKENS.bgWeak};
  --border: ${TOKENS.border};
  --border-strong: ${TOKENS.borderStrong};
  --text: ${TOKENS.text};
  --text-weak: ${TOKENS.textWeak};
  --text-muted: ${TOKENS.textMuted};

  position: relative;
  width: 100%;
  min-width: 300px;
  max-width: 420px;

  background: linear-gradient(
        90deg,
        color-mix(in srgb, var(--accent), #ffffff 30%) 0%,
        var(--accent) 18%,
        var(--accent) 82%,
        color-mix(in srgb, var(--accent), #ffffff 30%) 100%
      )
      top / 100% var(--stripe-h) no-repeat,
    var(--bg);

  border: 1px solid var(--border);
  border-top-color: transparent;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
  overflow: hidden;

  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  transition: transform 0.18s ease, box-shadow 0.18s ease,
    border-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08),
      0 0 0 3px color-mix(in srgb, var(--accent), transparent 85%);
    border-color: var(--border-strong);
  }
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: var(--text);
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StatusBadge = styled.span<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid ${({ $on }) => ($on ? "#86efac" : "#fca5a5")};
  background: ${({ $on }) => ($on ? "#dcfce7" : "#fee2e2")};
  color: ${({ $on }) => ($on ? "#166534" : "#991b1b")};

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

export const Meta = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  font-size: 12px;
`;

export const MetaItem = styled.div`
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 6px;

  .label {
    color: var(--text-muted);
    font-weight: 700;
  }

  .value {
    color: var(--text);
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/** Internet + Network 전용 한 줄 Row */
export const MetaRow = styled.div`
  display: flex;
  gap: 16px;

  > div {
    flex: 1;
    display: grid;
    grid-template-columns: 72px 1fr;
    gap: 6px;
  }

  .label {
    color: var(--text-muted);
    font-weight: 700;
  }

  .value {
    color: var(--text);
    font-weight: 700;
    white-space: nowrap;
  }
`;

export const Divider = styled.div`
  height: 1px;
  background: var(--border);
`;

export const Actions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: auto;
`;

export const Button = styled.button<{ $variant: "on" | "restart" }>`
  flex: 1;
  padding: 10px;
  border-color: #fff;
  border-radius: 12px;
  font-weight: 900;
  cursor: pointer;
  color: #fff;

  ${({ $variant }) =>
    $variant === "on"
      ? css`
          background: linear-gradient(180deg, #22c55e, #16a34a);
        `
      : css`
          background: linear-gradient(180deg, #6366f1, #4f46e5);
        `}
`;

export const GhostBtn = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 12px;
  background: var(--bg);
  border: 1px solid var(--border);
  font-weight: 900;
  cursor: pointer;
`;
